import { CSSProperties, FC, useEffect, useRef, useState } from "react";
import clsx from "classnames";
import {
  getYtbVideoIframeUrl,
  getYtbVideoThumbnail,
  sendYtbPlayerCommand,
  YtbThumbnailQuality,
} from "../../../models/media";
import { useStyles } from "./YtbVideoPlayer.style";
import { useCookieConsent } from "../../../hooks/useCookieConsent";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { isElementVisible } from "../../../models/screen";
import { isWebos } from "../../../utils/platform";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import PlayVideo1x from "../../../pages/GameDetails/assets/play_video_1x.webp";
import PlayVideo2x from "../../../pages/GameDetails/assets/play_video_2x.webp";
import PlayVideo3x from "../../../pages/GameDetails/assets/play_video_3x.webp";

const isVideoOnScreen = (element: HTMLElement | null) => {
  if (!element || !isElementVisible(element)) return false;

  const rect = element.getBoundingClientRect();
  return (
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
};

interface IYtbVideoPlayerProps {
  url: string;
  autoplay?: boolean;
  loop?: boolean;
  title?: string;
  className?: string;
  style?: CSSProperties;
  onError?: () => void;
  thumbnailQuality?: YtbThumbnailQuality;
  isPlayRequested?: boolean;
}

export const YtbVideoPlayer: FC<IYtbVideoPlayerProps> = ({
  url,
  autoplay = true,
  loop = true,
  title,
  className,
  style,
  onError,
  thumbnailQuality = "sd",
  isPlayRequested = false,
}) => {
  const classes = useStyles();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlayClicked, setIsPlayClicked] = useState(false);
  const { isFunctionalAllowed } = useCookieConsent();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isPausedRef = useRef(false);
  const { gamepadEmitter } = useGamepadContext();
  const canPlay = isFunctionalAllowed || isPlayRequested || isPlayClicked;

  useEffect(() => {
    if (!canPlay) return;
    const handleAction = (action: string) => {
      if (action !== "submit") return;
      if (!isVideoOnScreen(wrapperRef.current)) return;

      const nextPaused = !isPausedRef.current;
      sendYtbPlayerCommand(
        iframeRef.current,
        nextPaused ? "pauseVideo" : "playVideo",
      );
      isPausedRef.current = nextPaused;
    };

    gamepadEmitter.on("action", handleAction);
    return () => {
      gamepadEmitter.off("action", handleAction);
    };
  }, [gamepadEmitter, canPlay]);

  return (
    <div
      className={clsx(classes.wrapper, className)}
      style={style}
      ref={wrapperRef}
    >
      <img
        src={getYtbVideoThumbnail(url, thumbnailQuality)}
        className={classes.thumbnail}
        alt=""
      />
      {canPlay ? (
        <iframe
          ref={iframeRef}
          src={getYtbVideoIframeUrl(url, {
            autoplay,
            loop,
          })}
          className={clsx(
            classes.iframe,
            isLoaded && classes.iframeLoaded,
            isWebos && classes.iframeNoPointer,
          )}
          onLoad={() => {
            isPausedRef.current = false;
            setIsLoaded(true);
          }}
          onError={onError}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          tabIndex={isWebos ? -1 : undefined}
          title={title}
        />
      ) : (
        <div
          className={classes.playButton}
          data-gamepad="ytb-video-play"
          onClick={() => setIsPlayClicked(true)}
        >
          <StaticImage
            src={PlayVideo1x}
            src2x={PlayVideo2x}
            src3x={PlayVideo3x}
            className={classes.playIcon}
          />
        </div>
      )}
    </div>
  );
};
